import {
    createRouter,
    createWebHistory,
    NavigationGuardNext,
    RouteLocationNormalized,
    RouteRecordRaw,
} from 'vue-router';
import AssociateTrailerSuccess from '@/views/AssociateTrailerSuccess.vue';
import { resetBodyScrollLock } from '@/shared/bodyScrollLock';
import DamageReported from '@/views/DamageReported.vue';
import BodyDamage from '../views/TrailerAssessment/BodyDamage.vue';
import BrakeDamage from '../views/TrailerAssessment/BrakeDamage.vue';
import KingpinDamage from '../views/TrailerAssessment/KingpinDamage.vue';
import DamageComments from '../views/TrailerAssessment/DamageComments.vue';
import DriverLanding from '@/views/DriverLanding/DriverLanding.vue';
import DetachTrailerSuccess from '@/views/DetachTrailerSuccess.vue';
import Unauthorized from '@/views/DriverLanding/Unauthorized.vue';
import EleosDriverContainer from '@/views/EleosDriverContainer.vue';
import ElectricalDamage from '../views/TrailerAssessment/ElectricalDamage.vue';
import AirlinesElectricalDamage from '../views/TrailerAssessment/AirlinesElectricalDamage.vue';
import SelectTrailer from '../views/SelectTrailer/SelectTrailer.vue';
import EnterTrailer from '../views/EnterTrailer/EnterTrailer.vue';
import EnterTrailerRedirect from '../views/EnterTrailer/EnterTrailerRedirect.vue';
import FindTrailer from '../views/FindTrailer/FindTrailer.vue';
import TrailerAssessment from '../views/TrailerAssessment/TrailerAssessment.vue';
import TireDamage from '../views/TrailerAssessment/TireDamage.vue';
import VirtualRedTagEntry from '../views/VirtualRedTag/VirtualRedTagEntry.vue';
import VirtualRedTagManualEntry from '../views/VirtualRedTag/VirtualRedTagManualEntry.vue';
import VirtualRedTagVerifyTrailer from '../views/VirtualRedTag/VirtualRedTagVerifyTrailer.vue';
import VirtualRedTagScanQr from '../views/VirtualRedTag/VirtualRedTagScanQr.vue';
import VirtualRedTagConfirmation from '../views/VirtualRedTag/VirtualRedTagConfirmation.vue';
import VirtualRedTagUnavailable from '../views/VirtualRedTag/VirtualRedTagUnavailable.vue';
import VirtualRedTagRedTagged from '../views/VirtualRedTag/VirtualRedTagRedTagged.vue';
import VirtualRedTagNotificationMethod from '../views/VirtualRedTag/VirtualRedTagNotificationMethod.vue';
import VirtualRedTagNotificationContact from '../views/VirtualRedTag/VirtualRedTagNotificationContact.vue';
import VirtualRedTagNotificationConfirmed from '../views/VirtualRedTag/VirtualRedTagNotificationConfirmed.vue';
import {
    VIRTUAL_RED_TAG_QUERY_TRUE,
    VirtualRedTagNotificationQuery,
    VirtualRedTagNotificationRoute,
} from '../types/VirtualRedTagNotification';
import ConfirmPta from '@/views/ConfirmPta/ConfirmPta.vue';
import UpdatePta from '@/views/UpdatePta/UpdatePta.vue';
import globalConstants from '@/shared/globalConstants';
import { ActionTypes } from '@/store/actionTypes';
import { store } from '@/store';
import apmTransactions from '@/libraries/apm/transactions';
import EleosAuthenticationResponse from '@/types/EleosAuthenticationResponse';
import TrailerUnavailable from '@/views/TrailerUnavailable.vue';
import {
    canAccessProtectedRoute,
    isAuthorizationEnabled,
    isPublicRoute,
    driverAndCompanyFromQueryParams,
    getAuthorizeDriverResponseAndUpdateAlertBanner,
} from '@/router/helpers';
import FbaApiError from '@/types/FbaApiError';
import { WorkflowTypes } from '@/types/WorkflowTypes';

// A repeated query param (?a=1&a=2) arrives as an array; treat it as absent rather than
// passing an array to a string prop.
const singleQueryValue = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;

export const routes: Array<RouteRecordRaw> = [
    {
        path: '/pwaEntry',
        name: 'PWA Entry',
        beforeEnter: async () => {
            const authorizationResponse = await store.dispatch(
                ActionTypes.AUTHORIZE_DRIVER,
                undefined
            );

            if (authorizationResponse instanceof EleosAuthenticationResponse) {
                router.push({
                    name: 'DriverLanding',
                    params: {
                        company: authorizationResponse.company,
                        driverCode: authorizationResponse.username,
                    },
                });
            } else {
                const query =
                    authorizationResponse instanceof FbaApiError
                        ? {
                              type: authorizationResponse?.unauthorizedResponseTypes,
                          }
                        : undefined;
                router.push({
                    name: 'Unauthorized',
                    query,
                });
            }
        },
        component: Unauthorized,
    },
    {
        path: '/company/:company/driver/:driverCode',
        name: 'Driver',
        component: EleosDriverContainer,
        props: (route) => ({
            company: route.params.company,
            driverCode: route.params.driverCode,
        }),
        children: [
            {
                path: '',
                name: 'DriverLanding',
                component: DriverLanding,
                beforeEnter: () => {
                    store.dispatch(ActionTypes.SET_WORKFLOW_ORIGIN, undefined);
                },
            },
            {
                path: 'trailerUnavailable',
                name: 'TrailerUnavailable',
                component: TrailerUnavailable,
            },
            {
                path: 'findtrailer',
                name: 'FindTrailer',
                component: FindTrailer,
                props: (route) => ({
                    latitude: route.query.lat,
                    longitude: route.query.lng,
                }),
                beforeEnter: () => {
                    store.dispatch(
                        ActionTypes.SET_WORKFLOW_ORIGIN,
                        WorkflowTypes.findATrailer
                    );
                },
            },
            {
                path: 'selecttrailer',
                name: 'SelectTrailer',
                component: SelectTrailer,
                beforeEnter: () => {
                    store.dispatch(
                        ActionTypes.SET_WORKFLOW_ORIGIN,
                        WorkflowTypes.selectATrailer
                    );
                },
            },
            {
                path: 'entertrailer',
                name: 'EnterTrailer',
                component: EnterTrailer,
            },
            {
                path: 'virtualredtag',
                name: 'VirtualRedTagEntry',
                component: VirtualRedTagEntry,
                beforeEnter: () => {
                    store.dispatch(
                        ActionTypes.SET_WORKFLOW_ORIGIN,
                        WorkflowTypes.virtualRedTag
                    );
                },
            },
            {
                path: 'virtualredtag/manualentry',
                name: 'VirtualRedTagManualEntry',
                component: VirtualRedTagManualEntry,
            },
            {
                path: 'virtualredtag/verifytrailer/:trailerNumber',
                name: 'VirtualRedTagVerifyTrailer',
                component: VirtualRedTagVerifyTrailer,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    trailerCompany: route.query.trailerCompany,
                }),
            },
            {
                path: 'virtualredtag/scanqr',
                name: 'VirtualRedTagScanQr',
                component: VirtualRedTagScanQr,
            },
            {
                path: 'virtualredtag/confirmation/:trailerNumber',
                name: 'VirtualRedTagConfirmation',
                component: VirtualRedTagConfirmation,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    verified: route.query.verified === 'true',
                    // Without this a refresh re-looks-up the trailer under the driver's
                    // company rather than the trailer's own.
                    trailerCompany: route.query.trailerCompany,
                }),
            },
            {
                path: 'virtualredtag/unavailable/:trailerNumber',
                name: 'VirtualRedTagUnavailable',
                component: VirtualRedTagUnavailable,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    reason: route.query.reason,
                    trailerCompany: route.query.trailerCompany,
                }),
            },
            {
                path: 'virtualredtag/redtagged/:trailerNumber',
                name: 'VirtualRedTagRedTagged',
                component: VirtualRedTagRedTagged,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    // Needed to offer notifications; absent when the screen is
                    // reached some other way, which just hides the button.
                    assessmentId: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.assessmentId]
                    ),
                }),
            },
            {
                path: 'virtualredtag/notifications/method/:trailerNumber',
                name: VirtualRedTagNotificationRoute.method,
                component: VirtualRedTagNotificationMethod,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    assessmentId: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.assessmentId]
                    ),
                }),
            },
            {
                path: 'virtualredtag/notifications/contact/:trailerNumber',
                name: VirtualRedTagNotificationRoute.contact,
                component: VirtualRedTagNotificationContact,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    assessmentId: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.assessmentId]
                    ),
                    method: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.method]
                    ),
                }),
            },
            {
                path: 'virtualredtag/notifications/confirmed/:trailerNumber',
                name: VirtualRedTagNotificationRoute.confirmed,
                component: VirtualRedTagNotificationConfirmed,
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    assessmentId: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.assessmentId]
                    ),
                    method: singleQueryValue(
                        route.query[VirtualRedTagNotificationQuery.method]
                    ),
                    trailerAlreadyInShop:
                        singleQueryValue(
                            route.query[
                                VirtualRedTagNotificationQuery
                                    .trailerAlreadyInShop
                            ]
                        ) === VIRTUAL_RED_TAG_QUERY_TRUE,
                }),
            },
            {
                path: 'entertrailerredirect',
                name: 'EnterTrailerRedirect',
                component: EnterTrailerRedirect,
            },
            {
                path: 'associatetrailersuccess',
                name: 'AssociateTrailerSuccess',
                component: AssociateTrailerSuccess,
                props: (route) => ({
                    trailerCompany: route.query.trailerCompany,
                    trailerNumber: route.query.trailerNumber,
                    hasBodyDamage: route.query.hasBodyDamage,
                    association: route.query.association,
                }),
            },
            {
                path: 'detachtrailersuccess',
                name: 'DetachTrailerSuccess',
                component: DetachTrailerSuccess,
                props: (route) => ({
                    trailerNumber: route.query.trailerNumber,
                }),
            },
            {
                path: 'attachTrailerRedTag',
                name: 'AttachTrailerRedTag',
                component: DamageReported,
            },
            {
                path: 'trailerNotFitForDuty',
                name: 'TrailerNotFit',
                component: DamageReported,
                props: (route) => ({
                    wasNonDamageIssue: route.query.wasNonDamageIssue,
                }),
            },
            {
                path: 'trailer/:trailerNumber/trailerassessment',
                component: TrailerAssessment,
                name: 'TrailerAssessment',
                props: (route) => ({
                    trailerNumber: route.params.trailerNumber,
                    company: route.params.company,
                    association: route.query.association,
                    origin: route.query.origin,
                }),
            },
            {
                path: 'trailer/:trailerNumber/electricaldamage',
                name: 'ElectricalDamage',
                component: ElectricalDamage,
            },
            {
                path: 'trailer/:trailerNumber/bodydamage',
                name: 'BodyDamage',
                component: BodyDamage,
            },
            {
                path: 'trailer/:trailerNumber/brakedamage',
                name: 'BrakeDamage',
                component: BrakeDamage,
            },
            {
                path: 'trailer/:trailerNumber/tiredamage',
                name: 'TireDamage',
                component: TireDamage,
            },
            {
                path: 'trailer/:trailerNumber/kingpindamage',
                name: 'KingpinDamage',
                component: KingpinDamage,
            },
            {
                path: 'trailer/:trailerNumber/airlineselectricaldamage',
                name: 'AirlinesElectricalDamage',
                component: AirlinesElectricalDamage,
            },
            {
                path: 'trailer/:trailerNumber/otherAssessment/:subType',
                name: 'OtherAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.other,
                },
            },
            {
                path: 'trailer/:trailerNumber/airlinesAssessment/:subType',
                name: 'AirlinesAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes
                            .airlinesElectrical,
                },
            },
            {
                path: 'trailer/:trailerNumber/kingpinAssessment/:subType',
                name: 'KingpinAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.kingpinLanding,
                },
            },
            {
                path: 'trailer/:trailerNumber/brakeAssessment/:subType',
                name: 'BrakeAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.brakes,
                },
            },
            {
                path: 'trailer/:trailerNumber/damageAssessment/:subType',
                name: 'BodyDamageAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.damage,
                },
            },
            {
                path: 'trailer/:trailerNumber/electricalAssessment/:subType',
                name: 'ElectricalAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.lights,
                },
            },
            {
                path: 'trailer/:trailerNumber/tiresAssessment/:subType',
                name: 'TiresAssessment',
                component: DamageComments,
                props: {
                    assessmentType:
                        globalConstants.trailer.assessmentTypes.tires,
                },
            },
        ],
    },
    {
        // TODO AFBAPP-1837: Update Confirm PTA flow to work within standard TS route assumptions /company/<company>/driver/<driver>/<route>
        path: '/confirmPta',
        name: 'ConfirmPTA',
        component: ConfirmPta,
        props: driverAndCompanyFromQueryParams,
    },
    {
        // TODO AFBAPP-1837: ensure this fits within our standard route assumptions
        path: '/updatePta',
        name: 'UpdatePTA',
        component: UpdatePta,
    },
    {
        path: '/unauthorized',
        name: 'Unauthorized',
        component: Unauthorized,
        props: (route: RouteLocationNormalized) => ({
            company: route?.redirectedFrom?.params?.company,
        }),
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

// Safety net: none of the overlays that lock body scroll (ConfirmationOverlay,
// MultipleTrailerSelection) are meant to stay open across a navigation, so
// clearing the lock after every completed route change can't unlock a
// legitimate open overlay. It only guarantees a leaked lock never survives
// past the page where it leaked, instead of silently blocking scroll on
// every screen for the rest of the session.
router.afterEach(() => {
    resetBodyScrollLock();
});

if (isAuthorizationEnabled()) {
    router.beforeEach(
        async (
            to: RouteLocationNormalized,
            from: RouteLocationNormalized,
            next: NavigationGuardNext
        ) => {
            if (isPublicRoute(to)) {
                next();
                return;
            }

            const authorizationResponse =
                await getAuthorizeDriverResponseAndUpdateAlertBanner(
                    to.params?.company as string | undefined
                );

            if (
                authorizationResponse instanceof EleosAuthenticationResponse &&
                canAccessProtectedRoute(to)
            ) {
                next();
            } else {
                apmTransactions.instrumentUnauthorized(
                    to.params?.driverCode as string,
                    to.params?.company as string
                );

                const query =
                    authorizationResponse instanceof FbaApiError
                        ? {
                              type: authorizationResponse?.unauthorizedResponseTypes,
                          }
                        : undefined;

                next({
                    name: 'Unauthorized',
                    query,
                });
            }
        }
    );
}
export default router;
